import React, { useState } from "react";
import { Spin, Form, Button, message } from "antd";
import { CopyOutlined, EyeOutlined, CheckOutlined, DeleteOutlined } from "@ant-design/icons";

import "./AmazonPicConverter.css";
import { TabTitle } from "../../utils/GeneralFunction";
import axios from "axios";
import { BASEURL } from "../../constant/config";
import Swal from "sweetalert2";

const AmazonPicConverter = ({ token }) => {
  TabTitle("Amazon-Picture-Converter");

  const [form] = Form.useForm();

  const [images, setImages] = useState([]); // 👈 MULTIPLE IMAGES
  const [loading, setLoading] = useState(false);
  const [urlInput, setUrlInput] = useState("");

  console.log("images", images)
  // 🔹 HANDLE MULTIPLE IMAGE UPLOAD
  const handleUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setLoading(true);

    const promises = files.map((file) => {
      return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
          resolve({
            file,
            src: url,
            width: img.width,
            height: img.height,
            name: file.name,
            size: file.size,
            type: file.type,
            isUrl: false,
          });
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(null);
        };

        img.src = url;
      });
    });

    Promise.all(promises).then((results) => {
      setImages((prev) => [...prev, ...results.filter(Boolean)]);
      setLoading(false);
    });
  };


  // 🔹 HANDLE MULTIPLE URLS (comma / new line separated)
  const processUrls = () => {
    if (!urlInput.trim()) return;

    setLoading(true);

    const urls = urlInput
      .split(/\n|,/)
      .map((u) => u.trim())
      .filter(Boolean);

    const results = urls.map((url) => ({
      file: null,
      src: url,
      width: null,
      height: null,
      name: url.split("/").pop() || "image-url",
      size: null,
      type: null,
      isUrl: true,
    }));

    setImages((prev) => [...prev, ...results]);
    setLoading(false);
    setUrlInput("");
  };



  // uploadHandler
  const uploadHandler = () => {
    setLoading(true);
    console.log("Images", images);

    const formData = new FormData();

    // 🔹 Separate files & URLs
    images.forEach((img) => {
      if (img.isUrl) {
        formData.append("src", img.src);   // 👈 URL images
      } else if (img.file) {
        formData.append("images", img.file);  // 👈 File images
      }
    });

    axios({
      method: "POST",
      url: `${BASEURL}uploads/removeBg`,
      headers: {
        Authorization: "Bearer " + token,
      },
      data: formData,
    })
      .then((response) => {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data.message,
        });

        // 🔹 Map response URLs back to images
        setImages(
          images.map((img, idx) => ({
            ...img,
            s3_url: response.data.urls?.[idx] || null,
          }))
        );

        console.log("Response", response.data);
        setLoading(false);
      })
      .catch((error) => {
        const errorMsg =
          error?.response?.data?.error
            ? (() => {
              try {
                return JSON.parse(error.response.data.error)?.error?.message;
              } catch {
                return error.response.data.error;
              }
            })()
            : error?.response?.data?.message || error.message;

        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });

        setLoading(false);
      });
  };

  const copyHandler = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      message.success({
        content: "Copied to clipboard",
        duration: 1, // ⏱ 3 seconds
      });
    } catch (err) {
      message.error({
        content: "Copy failed",
        duration: 1,
      });
    }
  };

  const deleteImage = (index) => {
    setImages((prev) => {
      const img = prev[index];

      // blob memory cleanup
      if (img?.src?.startsWith("blob:")) {
        URL.revokeObjectURL(img.src);
      }

      return prev.filter((_, i) => i !== index);
    });
  };


  // 🔹 RESET ALL
  const reset = () => {
    images.forEach((img) => {
      if (img.src.startsWith("blob:")) URL.revokeObjectURL(img.src);
    });
    setImages([]);
  };

  return (
    <div className="amazn_pic_converter_main">
      <Spin spinning={loading} size="large">

        {/* 🔹 UPLOAD UI */}
        {images.length === 0 && (
          <>
            <div className="amazon_card">
              <div className="icon">✨</div>

              <h2>Background Remover</h2>
              <p>
                Upload multiple images or paste multiple links. Images will be
                processed and shown in a grid.
              </p>

              <label className="upload-btn">
                Upload Images
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleUpload}
                />
              </label>
            </div>

            <div className="divider">
              <span>OR PASTE LINKS</span>
            </div>

            <div className="url-box">
              <input
                placeholder="Paste multiple image URLs (comma or new line separated)"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
              />
              <button className="process-btn" onClick={processUrls}>
                Process URLs
              </button>
            </div>
          </>
        )}

        {/* 🔹 RESULT GRID */}
        {images.length > 0 && (
          <div className="result-card">
            <div className="result-header">
              <div>
                <h3>Perfectly Cleaned</h3>
                <span>{images.length} Images Processed</span>
              </div>
              <button className="new-image" onClick={reset}>
                + New Images
              </button>
            </div>

            <div className="image-grid">
              {images.map((img, index) => (
                <div className="grid-item">
                  <img src={img.src} alt={`Result ${index}`} />

                  {img.s3_url && (
                    <div className="copy_container">
                      <button
                        className="copy-btn"
                        onClick={() => copyHandler(img.s3_url)}
                      >
                        <CopyOutlined /> Copy
                      </button>

                      <a
                        className="view-btn"
                        href={img.s3_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <EyeOutlined /> View
                      </a>
                    </div>
                  )}

                  {/* 🔴 DELETE BUTTON */}
                  {
                    images.length > 1 && (
                      <button
                        className="delete-btn"
                        onClick={() => deleteImage(index)}
                      >
                        <DeleteOutlined />
                      </button>
                    )
                  }
                </div>

              ))}
            </div>

            {
              loading ? (<button className="upload-btn" disabled>
                Uploading...
              </button>) : (
                <button className="upload-btn" onClick={uploadHandler}>
                  Upload
                </button>
              )
            }
          </div>
        )}

      </Spin>
    </div>
  );
};

export default AmazonPicConverter;
